import React from 'react'
import {Box, Collapse, Link, Stack, Typography} from '@mui/material'
import {OPERATORS, optionsForItem} from '../../utils/skipLogic/conditions'

const itemLabel = item => {
  if (!item) return 'an item'
  const kind = item.kind ?? item.itemType
  if (kind === 1) return item.content?.headline?.trim() || 'Content screen'
  if (kind === 2) return 'Skip logic'
  return (item.staffText || item.parentText || item.pupilText || item.text || '').trim() || `Question ${item.id}`
}

const describeDestination = (goTo, byId) => {
  if (!goTo) return 'the next item'
  if (goTo.end === true) return 'End of survey'
  if (goTo.next === true) return 'the next item'
  if (goTo.item != null) return itemLabel(byId.get(goTo.item))
  return 'the next item'
}

// Why a chosen destination was refused and the default route used instead.
const FALLBACK_REASON = {
  'rules-unreadable': 'the rules could not be read',
  'destination-missing': 'the destination no longer exists',
  'destination-not-forward': 'the destination is not later in the survey',
  'destination-is-skip-logic': 'the destination is a skip-logic section',
}

// The value(s) an answer condition was tested against, as text.
const answerValueText = (node, item) => {
  const opKind = OPERATORS[node.op]?.kind
  if (opKind === 'number') return String(node.number)
  if (opKind === 'range') return `${node.min}–${node.max}`
  if (opKind === 'none') return ''
  const opts = optionsForItem(item)
  return (node.values || [])
    .map(v => opts.find(o => o.value === v)?.label ?? String(v))
    .join(', ')
}

// One condition line (or group) from the explainRoute `when` tree, with its
// matched / not-matched indicator and the values that were evaluated.
const Condition = ({node, byId, factLabels, depth = 0}) => {
  const mark = node.held ? '✓' : '✗'
  const cls = node.held ? 'cond-held' : 'cond-unheld'
  const indent = {pl: depth * 1.5}

  if (node.kind === 'group') {
    return (
      <Box className={cls} sx={indent}>
        <div>
          {mark} {node.mode === 'any' ? 'Any of:' : 'All of:'}
        </div>
        {node.conditions.map((c, i) => (
          <Condition key={i} node={c} byId={byId} factLabels={factLabels} depth={depth + 1} />
        ))}
      </Box>
    )
  }

  let text
  if (node.kind === 'answer') {
    const item = byId.get(node.item)
    const op = OPERATORS[node.op]?.label || node.op
    const value = answerValueText(node, item)
    text = `${itemLabel(item)} ${op}${value ? ` ${value}` : ''}`
  } else if (node.kind === 'fact') {
    text = factLabels[node.fact] || 'Participant/attribute condition'
  } else if (node.kind === 'participant') {
    const op = OPERATORS[node.op]?.label || node.op
    text = `Participant ${op} ${(node.values || []).join(', ')}`
  } else if (node.kind === 'attribute') {
    const op = OPERATORS[node.op]?.label || node.op
    text = `${node.key} ${op} ${(node.values || []).join(', ')}`
  } else {
    text = 'condition'
  }

  return (
    <Box className={cls} sx={indent}>
      {mark} {text}
    </Box>
  )
}

// Read-only view of the route the preview took, from explainRoute: the items
// shown, each skip-logic section reached with every rule's match state, the
// conditions it evaluated and the chosen destination, and the items skipped.
// factLabels describes projected persona conditions. `jumped` notes that the
// author jumped to an item, bypassing routing. Collapsible; hidden by default
// when the preview wires onToggle.
export default function RouteTracePanel({
  trace,
  items = [],
  factLabels = {},
  jumped = false,
  open = true,
  onToggle,
}) {
  const byId = new Map(items.map(it => [it.id, it]))
  const {shownItemIds = [], skippedItemIds = [], sections = []} = trace || {}

  return (
    <Box
      className="route-trace-panel"
      sx={{p: 2, borderLeft: '1px solid #D7E3E8', minWidth: 260, fontSize: 14}}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
        <Typography variant="subtitle2">Route trace</Typography>
        {onToggle && (
          <Link component="button" type="button" underline="hover" onClick={onToggle}>
            {open ? 'Hide' : 'Show'}
          </Link>
        )}
      </Stack>

      <Collapse in={open} unmountOnExit>
        {jumped && (
          <Box className="route-trace-jumped" sx={{mb: 1, color: '#8A6D1B'}}>
            Jumped to an item — routing was bypassed for the jump.
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          Shown so far
        </Typography>
        <Box component="ol" sx={{pl: 2, mt: 0.5, mb: 1}}>
          {shownItemIds.map(id => (
            <li key={id}>{itemLabel(byId.get(id))}</li>
          ))}
        </Box>

        {sections.map((section, i) => (
          <Box key={i} className="route-trace-section" sx={{mb: 1}}>
            <Typography variant="caption" color="text.secondary">
              Skip logic
            </Typography>
            {section.unreadable ? (
              <div className="rule-unreadable">
                Rules could not be read — not evaluated, continued to the next item.
              </div>
            ) : (
              section.rules.map((rule, ruleIndex) => (
                <Box key={rule.id} className="route-trace-rule" sx={{mb: 0.5}}>
                  {/* Rules are numbered as the section editor numbers them; their ids mean nothing to an author. */}
                  <div className={rule.id === section.matchedRule ? 'rule-matched' : 'rule-unmatched'}>
                    {rule.matched ? '✓' : '✗'} Rule {ruleIndex + 1} → {describeDestination(rule.goTo, byId)}
                    {rule.id === section.matchedRule ? ' (chosen)' : ''}
                  </div>
                  <Box sx={{pl: 1.5, color: 'text.secondary'}}>
                    <Condition node={rule.when} byId={byId} factLabels={factLabels} />
                  </Box>
                </Box>
              ))
            )}
            {!section.unreadable && section.matchedRule == null && (
              <div className="rule-otherwise">
                No rule matched → otherwise: {describeDestination(section.destination, byId)}
              </div>
            )}
            {section.fallback && FALLBACK_REASON[section.fallback] && (
              <Box className="rule-fallback" sx={{color: '#8A6D1B'}}>
                Destination unavailable ({FALLBACK_REASON[section.fallback]}) — used {describeDestination(section.destination, byId)}.
              </Box>
            )}
          </Box>
        ))}

        {skippedItemIds.length > 0 && (
          <Box className="route-trace-skipped">
            <Typography variant="caption" color="text.secondary">
              Skipped
            </Typography>
            <Box component="ul" sx={{pl: 2, mt: 0.5}}>
              {skippedItemIds.map(id => (
                <li key={id}>{itemLabel(byId.get(id))}</li>
              ))}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  )
}
